import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js"
import { formatValue } from "react-currency-input-field"

import type { ErrorType } from "@/lib/clients"

import type { FieldValues, Path, UseFormSetError } from "react-hook-form"
const genericErrorMsg = "Sorry, something went wrong. Please try again."

export type AllauthResponseErrorItem = {
  attr: string
  detail: string
}

export type BoundlessResponseErrorItem = {
  code: string
  message: string
  param?: string
}

export type ErrorItemType =
  | AllauthResponseErrorItem[]
  | BoundlessResponseErrorItem[]

export type ErrorResponseType = {
  status?: number
  errors?: ErrorItemType
}

type FormErrors = Record<string, { message: string }[]>

function isBoundlessError(err: unknown): err is BoundlessResponseErrorItem {
  return (
    typeof err === "object" &&
    err !== null &&
    ("message" in err || "param" in err)
  )
}

function isAllauthError(err: unknown): err is AllauthResponseErrorItem {
  return (
    typeof err === "object" &&
    err !== null &&
    ("detail" in err || "attr" in err)
  )
}

/**
 * Populates field errors from an API error response.
 * Supports:
 * - Boundless format (param/message)
 * - Allauth format (attr/detail)
 * - DRF-style { field: [messages] } errors
 * - Nested serializer errors (recursively flattened with dot-notation)
 * @param error The error object from the API client.
 * @returns A record mapping field names to arrays of error messages.
 */
export function populateFieldErrors(
  error: ErrorType<ErrorResponseType>,
): FormErrors {
  if (!error.response?.data) {
    return { root: [{ message: error.message || genericErrorMsg }] }
  }

  /**
   * Recursively flattens nested DRF-style error objects.
   * e.g. { profile: { first_name: ["msg"] } }
   *  -> { "profile.first_name": [{ message: "msg" }] }
   */
  function flattenErrors(
    data: unknown,
    parentKey = "",
    acc: FormErrors = {},
  ): FormErrors {
    if (Array.isArray(data)) {
      acc[parentKey || "root"] = data.map((msg) => ({
        message: typeof msg === "string" ? msg : "Unknown error",
      }))
    } else if (data && typeof data === "object") {
      Object.entries(data).forEach(([key, value]) => {
        const fullKey =
          key === "non_field_errors"
            ? "root"
            : parentKey
              ? `${parentKey}.${key}`
              : key
        flattenErrors(value, fullKey, acc)
      })
    }
    return acc
  }

  // Handle DRF-style object (including nested serializers)
  if (
    error.response.data &&
    typeof error.response.data === "object" &&
    !Array.isArray(error.response.data.errors)
  ) {
    const fieldErrors = flattenErrors(error.response.data)
    if (Object.keys(fieldErrors).length > 0) return fieldErrors
  }

  // Handle Boundless/Allauth error arrays
  if (Array.isArray(error.response.data.errors)) {
    const fieldErrors: FormErrors = {}
    error.response.data.errors.forEach((err) => {
      let fieldName: string
      let message: string

      if (isBoundlessError(err)) {
        fieldName = err.param || "root"
        message = err.message || "Unknown error"
      } else if (isAllauthError(err)) {
        fieldName = err.attr || "root"
        message = err.detail || "Unknown error"
      } else {
        fieldName = "root"
        message = "Unknown error"
      }

      if (fieldName === "non_field_errors") {
        fieldName = "root"
      }

      if (!fieldErrors[fieldName]) fieldErrors[fieldName] = []
      fieldErrors[fieldName].push({ message })
    })
    return fieldErrors
  }

  // Fallback
  return { root: [{ message: error.message || genericErrorMsg }] }
}

export function applyErrorsToForm<T extends FieldValues>(
  setError: UseFormSetError<T>,
  error: ErrorType<ErrorResponseType>,
  prefix?: Path<T>,
) {
  const errors = populateFieldErrors(error)
  Object.entries(errors).forEach(([field, errorArr]) => {
    errorArr.forEach((error) => {
      const fieldPath = (prefix ? `${prefix}.${field}` : field) as Path<T>
      try {
        setError(fieldPath, error)
      } catch (e) {
        console.warn(
          `Field ${field} not found in form, adding to root error`,
          e,
        )
        if (field !== "root") {
          setError("root" as Path<T>, {
            message: `${field}: ${error.message}`,
          })
        }
      }
    })
  })
}

function formatToE164(phone: string, countryCode: CountryCode): string | null {
  const phoneNumber = parsePhoneNumberFromString(phone, countryCode)
  return phoneNumber?.format("E.164") || null
}
export function formatAndValidatePhone(
  phone: string,
  country: CountryCode,
): string {
  const formatted = formatToE164(phone, country)
  if (!formatted) {
    throw new Error(`Invalid phone number`)
  }
  return formatted
}

export function formatPhoneForDisplay(phone: string): string {
  const digitsOnly = phone.replace(/\D/g, "").replace(/^1/, "")
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`
  }
  return phone
}

export function formatCurrency(value: string): string {
  return formatValue({
    value,
    intlConfig: {
      locale: "en-US",
      currency: "USD",
    },
  })
}

/**
 * Given a funding value in dollars, returns a short version of the value in
 * either dollars, thousands of dollars ('k'), or millions of dollars ('MM').
 * If the input value is null, returns "-".
 *
 * @param value A number representing the funding value in dollars, or null.
 * @returns A string representing the shortened funding value.
 */
export function shortenFundingValue(value: number | null): string {
  if (value == null) {
    return "-"
  }
  if (value < 1000) {
    return `${value}`
  } else if (10 ** 3 <= value && value < 10 ** 6) {
    return `${Math.floor(value / 10 ** 3)}k`
  } else {
    return `${Math.floor(value / 10 ** 6)}MM`
  }
}
